import { AfterViewInit, Directive, Input, OnDestroy } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UserPrefsService } from '../services/user-prefs.service';

/**
 * Persists a paginator page size for the current user.
 *
 * Apply this directive to any `mat-paginator` and pass a stable page key.
 * The directive restores the last selected size after login and saves any
 * subsequent user-driven changes back through the existing preference store.
 */
@Directive({
  selector: 'mat-paginator[appPersistPageSize]',
})
export class PersistPageSizeDirective implements AfterViewInit, OnDestroy {
  @Input('appPersistPageSize') pageKey = '';
  @Input() appPersistPageSizeDefault?: number;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly paginator: MatPaginator,
    private readonly userPrefs: UserPrefsService,
  ) { }

  ngAfterViewInit(): void {
    const preferredSize = this.getPreferredPageSize();
    this.paginator.pageSize = preferredSize;

    this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe((event: PageEvent) => {
      this.userPrefs.set(this.pageKey, 'paginator', 'pageSize', event.pageSize);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getPreferredPageSize(): number {
    const savedSize = this.userPrefs.get<number>(this.pageKey, 'paginator', 'pageSize');
    const defaultSize = this.resolveDefaultPageSize();

    if (this.isAllowedPageSize(savedSize)) {
      return savedSize;
    }

    return defaultSize;
  }

  private resolveDefaultPageSize(): number {
    if (this.isValidPageSize(this.appPersistPageSizeDefault)) {
      return this.appPersistPageSizeDefault as number;
    }

    return this.isValidPageSize(this.paginator.pageSize) ? this.paginator.pageSize : 10;
  }

  private isAllowedPageSize(value: number | null | undefined): value is number {
    if (!this.isValidPageSize(value)) {
      return false;
    }

    const allowedPageSizes = this.paginator.pageSizeOptions ?? [];
    return allowedPageSizes.length === 0 || allowedPageSizes.includes(value);
  }

  private isValidPageSize(value: number | null | undefined): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
  }
}
